import UserHelper from '@shared/helpers/user-helper';
import { Offer } from '@schemas/account/offer.interface';
import { Product } from '@schemas/product.interface';
import { Seller } from '@schemas/seller.interface';
import { APP_CONSTANTS } from '@shared/constants/app-constants';
import { CurrencyCodeEnum } from '@shared/enums/currency.enum';
import { OfferUserPerspectiveEnum } from '@shared/enums/offers.enum';
import { ProductCategoriesStringEnum } from '@shared/enums/product-categories-string.enum';
import { ProductConditionsStringEnum } from '@shared/enums/product-conditions-string.enum';
import { SellerTypeStringEnum } from '@shared/enums/seller-type-string.enum';
import DateUtils from '@shared/utils/date-utils';
import { addDays, differenceInMinutes, parseISO } from 'date-fns';

/**
 * Helper class for 'Make an Offer' feature
 */
export default class OfferHelper {
  /**
   * Given an offer, gets the time left to such offer expire.
   *
   * @param {Offer} offer
   * @returns {number} - Time left to expire in minutes, two decimal places
   */
  static getTimeToExpireValue(offer: Offer): number {
    const offerCreatedAt: Date = parseISO(offer?.createdAt.toString());
    const nowUTC: Date = parseISO(DateUtils.DiscardTimezone(new Date()));

    return +(
      differenceInMinutes(addDays(offerCreatedAt, OfferHelper.calculateExpirationDays(offerCreatedAt)), nowUTC) / 60
    ).toFixed(2);
  }

  /**
   * Given a date, calculates how may days will be the expiration time amount (in days).
   *
   * All week days should have expiration time of 48h (two days),
   * except for Friday which expires in 72h (three days)
   *
   * @param {Date} createdAt - Date to be calculated
   * @returns {number} - Number of days to expiration date
   */
  static calculateExpirationDays(createdAt: Date): number {
    if (createdAt.getDay() === 5) {
      // in case of Friday
      return 3;
    }
    else {
      // All week days, except Friday
      return 2;
    }
  }

  static getExpiredHoursValue(offer: Offer): number {
    return OfferHelper.getTimeToExpireValue(offer) * -1;
  }

  static hasOfferExpired(offer: Offer): boolean {
    return OfferHelper.getTimeToExpireValue(offer) <= 0;
  }

  static isBuyerOffer(offerPosition: number): boolean {
    return offerPosition % 2 > 0;
  }

  static isSellerOffer(offerPosition: number): boolean {
    return offerPosition % 2 === 0;
  }

  static isBuyerPerspective(userPerspective: OfferUserPerspectiveEnum): boolean {
    return userPerspective === OfferUserPerspectiveEnum.BUYER;
  }

  static isSellerPerspective(userPerspective: OfferUserPerspectiveEnum): boolean {
    return userPerspective === OfferUserPerspectiveEnum.SELLER;
  }

  static getPreviousOfferAmount(offerHistory: Offer[], currencyCode: CurrencyCodeEnum): number {
    const lastOffer: Offer = [...offerHistory].shift();

    if (lastOffer.position > 1) {
      return offerHistory[1].price[currencyCode.toLowerCase()].value;
    }
    else {
      return null;
    }
  }

  static isLastOffer(userPerspective: OfferUserPerspectiveEnum, lastOfferPosition: number): boolean {
    return (
      (userPerspective === OfferUserPerspectiveEnum.BUYER && lastOfferPosition === 4)
      || (userPerspective === OfferUserPerspectiveEnum.SELLER && lastOfferPosition === 5)
    );
  }
}
